import { ChangeEvent } from 'react';

import { Label } from '@/components/shared/Label/Label';
import { cn } from '@/lib/utils';

interface MoneyInputProps {
  label?: string;
  /** Usado quando o campo não tem label visível. */
  ariaLabel?: string;
  /** Valor em centavos; `null` deixa o campo vazio. */
  value: number | null;
  currency?: string;
  subLabel?: string;
  helperText?: string;
  disabled?: boolean;
  /** Campo calculado: mostra o valor, sem deixar editar. */
  readOnly?: boolean;
  dataCy?: string;
  onChange?: (valueInCents: number) => void;
}

/** "R$", "US$" — o mesmo símbolo que o `formatCurrency` usa. */
function currencySymbol(currency: string): string {
  return (
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value ?? currency
  );
}

const amountFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Campo de dinheiro com o símbolo da moeda num prefixo à esquerda. */
export function MoneyInput({
  label,
  ariaLabel,
  value,
  currency = 'BRL',
  subLabel,
  helperText,
  disabled,
  readOnly,
  dataCy,
  onChange,
}: MoneyInputProps) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, '');

    onChange?.(parseInt(digits || '0', 10));
  };

  return (
    <div className='grid w-full items-center gap-2'>
      {label && <Label className='!text-white'>{label}</Label>}
      <div
        className={cn(
          'flex h-10 w-full items-center overflow-hidden rounded-md border bg-[#09090B]',
          helperText ? 'border-borderError' : 'border-colorBorder',
          disabled && 'bg-bgDisabled opacity-50'
        )}
      >
        <span className='flex h-full shrink-0 items-center border-r border-colorBorder px-3 text-sm font-medium text-zinc-400'>
          {currencySymbol(currency)}
        </span>
        <input
          aria-label={ariaLabel ?? label}
          value={value === null ? '' : amountFormatter.format(value / 100)}
          placeholder='0,00'
          onChange={handleChange}
          disabled={disabled}
          readOnly={readOnly}
          data-cy={dataCy}
          inputMode='numeric'
          className={cn(
            'h-full w-full min-w-0 bg-transparent px-3 text-white focus-visible:outline-none disabled:cursor-not-allowed',
            readOnly && 'text-zinc-400'
          )}
        />
      </div>
      {subLabel && <p className='text-sm text-zinc-500'>{subLabel}</p>}
      {helperText && <p className='text-sm text-error'>{helperText}</p>}
    </div>
  );
}
